import SortOrders from "./sort-orders";
import Parameter from "./parameter";
import MigrationMapping from "./migration-mapping";
import Msg from "./message";
import Entity from "./entity";
import EmailTemplate from "./email-template";
import { clone, sortByOrder } from "../utils";
import IPublicForm from "../interface/public-form";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IEmailTemplate from "../interface/email-template";
import PublicFormType from "../enum/public-form-type";
import ParamStorage from "../enum/param-storage";
import Message from "../enum/message";
import { PublicFormTypes } from "../enum/field-types";
import ParameterStorage from "../class/parameter-storage";

class PublicFormInstance {

    private readonly Store = ParamStorage.PublicForms;

    public getAll(entityId: number | null = null): IPublicForm[] {
        const all: IPublicForm[] = this.getStore().getAll();
        if (entityId) {
            const order: number[] = SortOrders.get(this.Store, entityId);
            const result: IPublicForm[] = all.filter(a => a.entityId === entityId);;
            return sortByOrder(result, 'id', order);
        }
        return all;
    }

    public get(id: number): IPublicForm | null {
        return this.getStore().get(id);
    }

    public async create(form: IPublicForm, notifs: IEmailTemplate[] = []): Promise<IPublicForm> {
        form.notifications = await this.setNotifications(0, notifs);
        return this.getStore().create(form);
    }

    public async update(form: IPublicForm, notifs: IEmailTemplate[] = []): Promise<IPublicForm> {
        form.notifications = await this.setNotifications(form.id as number, notifs);
        return this.getStore().update(form);
    }

    public async delete(id: number): Promise<number> {
        await this.deleteNotifications(id);
        return this.getStore().delete(id);
    }

    public async clone(id: number): Promise<IPublicForm | null> {
        const form: IPublicForm | null = this.get(id);
        if (form) {
            const cloneForm: IPublicForm = clone(form);
            delete cloneForm.id;
            cloneForm.name = `${Msg.get(Message.copy)} ${cloneForm.name}`;
            const notifications: IEmailTemplate[] = [];
            for (let i = 0; i < form.notifications.length; i++) {
                const notif: IEmailTemplate | null = await EmailTemplate.load(form.notifications[i]);
                if (notif) {
                    const cloneNotif: IEmailTemplate = clone(notif);
                    delete cloneNotif.id;
                    notifications.push(cloneNotif);
                }
            }
            return this.create(cloneForm, notifications);
        }
        return null;
    }

    public getFields(entityId: number): IField[] {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            return entity.fields.filter((f: IField) => PublicFormTypes.includes(f.uiType));
        }
        return [];
    }

    public getEmailOptions(entityId: number): IPublicForm[] {
        return this.getAll().filter((form: IPublicForm) => {
            if (form.type === PublicFormType.Update) {
                return form.entityId === entityId;
            }
            return true;
        });
    }

    public async getByMapping(oldId: string): Promise<IPublicForm | null> {
        // TODO - Delete this old shit
        const newId: number | null = await MigrationMapping.get(this.Store, oldId);
        if (newId !== null) {
            return this.get(newId);
        }
        return null;
    }

    public async loadNotifications(id: number): Promise<IEmailTemplate[]> {
        const result: IEmailTemplate[] = [];
        const form: IPublicForm | null = this.get(id);
        if (form) {
            for (let i = 0; i < form.notifications.length; i++) {
                const tpl: IEmailTemplate | null = await EmailTemplate.load(form.notifications[i]);
                if (tpl) {
                    result.push(tpl);
                }
            }
        }
        return result;
    }

    public async setOrder(entityId: number | null = null, order: number[]): Promise<IPublicForm[]> {
        await SortOrders.set({ param: this.Store, entityId, order });
        return this.getAll(entityId);
    }

    /* Private Methods */

    private async setNotifications(id: number, tpls: IEmailTemplate[]): Promise<number[]> {
        let toCreate: IEmailTemplate[] = [];
        const toUpdate: IEmailTemplate[] = [];
        const toDelete: number[] = [];
        if (id) {
            const form: IPublicForm | null = this.get(id);
            if (form) {
                form.notifications.forEach((tplId: number) => {
                    const tpl: IEmailTemplate | undefined = tpls.find(i => i.id === tplId);
                    if (tpl) {
                        toUpdate.push(tpl);
                    } else {
                        toDelete.push(tplId);
                    }
                });
                tpls.forEach((tpl: IEmailTemplate) => {
                    if (!tpl.id) {
                        toCreate.push(tpl);
                    }
                });
            }
        } else {
            toCreate = tpls;
        }
        const created: IEmailTemplate[] = await EmailTemplate.bulk(toCreate, toUpdate, toDelete);
        return [...created.map(tpl => tpl.id as number), ...toUpdate.map(tpl => tpl.id as number)];
    }

    private async deleteNotifications(id: number): Promise<void> {
        const form: IPublicForm | null = this.get(id);
        if (form) {
            for (let i = 0; i < form.notifications.length; i++) {
                await EmailTemplate.delete(form.notifications[i]);
            }
        }
    }

    /* Private Methods - Parameter Storage */

    private getStore(): ParameterStorage<IPublicForm> {
        return Parameter.get(this.Store);
    }

}

const PublicForm = new PublicFormInstance();

export default PublicForm;